/**
 * V0.2 静态演示数据（不连接口，形状=types.ts 的接口契约）
 * 商品/价格示例来自原型演示数据（低保真 · 排期价格为演示）
 */
import {
  Anchor,
  Category,
  Recommendation,
  Session,
  SessionProduct,
} from './types';

/* 时间基线：今天（本地时间，即演示环境的北京时间口径） */
function fmtDate(d: Date): string {
  const p = (v: number) => String(v).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}
export function todayStr(): string {
  return fmtDate(new Date());
}
function addDays(base: string, n: number): string {
  const [y, m, d] = base.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + n);
  return fmtDate(dt);
}
export function dateRange(count: number): string[] {
  const t = todayStr();
  return Array.from({ length: count }, (_, i) => addDays(t, i));
}
export const WEEKDAYS = ['周日', '周一', '周二', '周三', '周四', '周五', '周六'];
export function weekOf(dateStr: string): string {
  return WEEKDAYS[new Date(dateStr + 'T00:00:00').getDay()];
}

export const CATEGORIES: Category[] = [
  { id: 'all', label: '全部' },
  { id: 'beauty', label: '美妆护肤' },
  { id: 'food', label: '食品保健' },
  { id: 'home', label: '日用家居' },
  { id: 'fashion', label: '服饰鞋包' },
];

export const ANCHORS: Anchor[] = [
  { id: 1, name: '李佳琦', avatar: '', tags: '美妆护肤 · 生活好物', listTemplate: 'main', scene: 'lijiaqi' },
  { id: 2, name: '所有女生', avatar: '', tags: '惊喜好物 · 日用家居', listTemplate: 'compact', scene: 'girls' },
  { id: 3, name: '所有女生的衣橱', avatar: '', tags: '服饰穿搭 · 品质生活', listTemplate: 'compact', scene: 'girls_wardrobe' },
];

/* ===== 场次（样例：今天三主播各 1 场 + 明天 2 场） ===== */
export const SESSIONS: Session[] = [
  { id: 's-1-08', anchorId: 1, date: todayStr(), startTime: '08:00', endTime: '14:00', status: 'ended', theme: '早场好物精选', updatedAt: '10:30' },
  { id: 's-1-19', anchorId: 1, date: todayStr(), startTime: '19:30', endTime: '01:00', status: 'living', theme: '秋日焕新 · 美妆生活专场', updatedAt: '10:30' },
  { id: 's-2-20', anchorId: 2, date: todayStr(), startTime: '20:00', endTime: '01:00', status: 'pending', theme: '惊喜好物 · 日用囤货专场', updatedAt: '10:30' },
  { id: 's-3-20', anchorId: 3, date: todayStr(), startTime: '20:30', endTime: '01:00', status: 'pending', theme: '秋冬衣橱 · 新品抢先看', updatedAt: '10:30' },
  { id: 's-1-14', anchorId: 1, date: addDays(todayStr(), 1), startTime: '19:30', endTime: '01:00', status: 'pending', theme: '焕新日 · 重磅彩妆', updatedAt: '10:30' },
  { id: 's-2-14', anchorId: 2, date: addDays(todayStr(), 1), startTime: '20:00', endTime: '01:00', status: 'pending', theme: '家居焕新囤货', updatedAt: '10:30' },
  // T31 改期演示：原排期在明天，实际改到后天 15:00
  { id: 's-3-14', anchorId: 3, date: addDays(todayStr(), 1), startTime: '15:00', endTime: '20:00', status: 'pending', theme: '秋冬穿搭返场', rescheduledTo: { date: addDays(todayStr(), 2), time: '15:00' }, updatedAt: '11:05' },
  // T32 取消演示
  { id: 's-1-14b', anchorId: 1, date: addDays(todayStr(), 1), startTime: '10:00', endTime: '12:00', status: 'pending', theme: '早场好物（已取消）', cancelled: true, updatedAt: '11:05' },
];

export function anchorOf(anchorId: number): Anchor {
  return ANCHORS.find((a) => a.id === anchorId) ?? ANCHORS[0];
}

/* ===== 场次商品（李佳琦主赠卡样例，仿原型演示商品） ===== */
/* 占位演示图（静态阶段统一占位；真实接入后换 tk_image 完整域名图） */
const DEMO_IMG = 'data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22600%22%20height%3D%22600%22%3E%3Cdefs%3E%3ClinearGradient%20id%3D%22gm%22%20x1%3D%220%22%20y1%3D%220%22%20x2%3D%220%22%20y2%3D%221%22%3E%3Cstop%20offset%3D%220%22%20stop-color%3D%22%23fdeef2%22%2F%3E%3Cstop%20offset%3D%221%22%20stop-color%3D%22%23f9dbe2%22%2F%3E%3C%2FlinearGradient%3E%3C%2Fdefs%3E%3Crect%20width%3D%22600%22%20height%3D%22600%22%20fill%3D%22url%28%2523gm%29%22%2F%3E%3Ccircle%20cx%3D%22300%22%20cy%3D%22250%22%20r%3D%22110%22%20fill%3D%22%23ffffff%22%20opacity%3D%220.9%22%2F%3E%3Crect%20x%3D%22245%22%20y%3D%22175%22%20width%3D%22110%22%20height%3D%22165%22%20rx%3D%2224%22%20fill%3D%22%23f3c7d2%22%2F%3E%3Crect%20x%3D%22273%22%20y%3D%22120%22%20width%3D%2254%22%20height%3D%2266%22%20rx%3D%2210%22%20fill%3D%22%23e9a7b8%22%2F%3E%3Ctext%20x%3D%22300%22%20y%3D%22470%22%20font-size%3D%2234%22%20fill%3D%22%23cf8fa0%22%20text-anchor%3D%22middle%22%20font-family%3D%22sans-serif%22%3E%E5%95%86%E5%93%81%E7%A4%BA%E6%84%8F%3C%2Ftext%3E%3C%2Fsvg%3E';
const DEMO_GIFT = 'data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22300%22%20height%3D%22300%22%3E%3Cdefs%3E%3ClinearGradient%20id%3D%22g2%22%20x1%3D%220%22%20y1%3D%220%22%20x2%3D%220%22%20y2%3D%221%22%3E%3Cstop%20offset%3D%220%22%20stop-color%3D%22%23fff3e6%22%2F%3E%3Cstop%20offset%3D%221%22%20stop-color%3D%22%23fbe2c2%22%2F%3E%3C%2FlinearGradient%3E%3C%2Fdefs%3E%3Crect%20width%3D%22300%22%20height%3D%22300%22%20fill%3D%22url%28%23g2%29%22%2F%3E%3Crect%20x%3D%2290%22%20y%3D%2280%22%20width%3D%22120%22%20height%3D%22130%22%20rx%3D%2218%22%20fill%3D%22%23ffffff%22%20opacity%3D%220.9%22%2F%3E%3Ctext%20x%3D%22150%22%20y%3D%22230%22%20font-size%3D%2230%22%20fill%3D%22%23c98a4b%22%20text-anchor%3D%22middle%22%20font-family%3D%22sans-serif%22%3E%E8%B5%A0%E5%93%81%E7%A4%BA%E6%84%8F%3C%2Ftext%3E%3C%2Fsvg%3E';

let _id = 1;
const idNext = () => `sp-${_id++}`;

/* -- 场次 s-1-19（李佳琦今晚，主赠卡） -- */
export const GOODS_TODAY_LI = buildGoods();

function buildGoods(): SessionProduct[] {
  const s = SESSIONS[1];
  return [
    {
      sessionProductId: idNext(),
      session: s,
      linkNo: 1,
      itemId: 'demo-1001',
      itemName: '万益蓝 WonderLab 益生菌即食乳酸菌',
      brand: '万益蓝',
      categoryId: 'food',
      image: DEMO_IMG,
      offers: [
        {
          specId: 'o-1001-1', name: '30瓶装', planGroup: null,
          buyCount: 1, toHandQty: 30, toHandUnit: '瓶',
          price: '199', condition: '拍1件，领取店铺券20元',
          gift: { has: true, desc: '谷物棒25g × 3根 × 2盒；膳食纤维饮10ml × 10条', images: [DEMO_GIFT] },
        },
        {
          specId: 'o-1001-2', name: '90瓶装', planGroup: null,
          buyCount: 1, toHandQty: 90, toHandUnit: '瓶',
          price: '559', condition: '拍1件，领取店铺券40元',
          gift: { has: true, desc: '膳食纤维饮10ml × 10条 × 3盒', images: [DEMO_GIFT] },
        },
        {
          specId: 'o-1001-3', name: '210瓶装 · 方案一', planGroup: 'plan-210',
          buyCount: 3, toHandQty: 210, toHandUnit: '瓶',
          price: '1129', condition: '拍3组，领取店铺券60元',
          gift: { has: true, desc: '谷物棒25g × 3盒 + 洗手液450ml × 1瓶', images: [DEMO_GIFT] },
        },
        {
          specId: 'o-1001-4', name: '210瓶装 · 方案二', planGroup: 'plan-210',
          buyCount: 3, toHandQty: 210, toHandUnit: '瓶',
          price: '1129', condition: '拍3组，领取店铺券60元',
          gift: { has: true, desc: '膳食纤维饮10ml × 20条 + 保温杯 × 1个', images: [DEMO_GIFT] },
        },
      ],
    },
    {
      sessionProductId: idNext(),
      session: s,
      linkNo: 2,
      itemId: 'demo-1002',
      itemName: '爱乐维活性叶酸复合维生素片',
      brand: '爱乐维',
      categoryId: 'food',
      image: DEMO_IMG,
      offers: [
        {
          specId: 'o-1002-1', name: '16周量 · 28片 × 4盒', planGroup: null,
          buyCount: 1, toHandQty: 112, toHandUnit: '片',
          price: '799', condition: '拍1件，活动价',
          gift: { has: false, desc: null, images: [] },
        },
        {
          specId: 'o-1002-2', name: '24周量 · 28片 × 6盒', planGroup: null,
          buyCount: 1, toHandQty: 168, toHandUnit: '片',
          price: '1079', condition: '拍1件，活动价',
          gift: { has: true, desc: '复合维生素片28片 × 1盒', images: [DEMO_GIFT] },
        },
      ],
    },
    {
      sessionProductId: idNext(),
      session: s,
      linkNo: 381,
      itemId: 'demo-1003',
      itemName: '欧莱雅精华面膜组合 15片装',
      brand: '欧莱雅',
      categoryId: 'beauty',
      image: DEMO_IMG,
      offers: [
        {
          specId: 'o-1003-1', name: '精华面膜组合 · 15片', planGroup: null,
          buyCount: 1, toHandQty: 15, toHandUnit: '片',
          price: '369', condition: '拍1件，活动价',
          gift: { has: true, desc: '旅行装精华2支', images: [DEMO_GIFT] },
        },
      ],
    },
    {
      sessionProductId: idNext(),
      session: s,
      linkNo: 382,
      itemId: 'demo-1004',
      itemName: '修丽可焕新精华组合',
      brand: '修丽可',
      categoryId: 'beauty',
      image: DEMO_IMG,
      offers: [
        {
          specId: 'o-1004-1', name: '焕新组合 · 30ml', planGroup: null,
          buyCount: 1, toHandQty: 1, toHandUnit: '套',
          price: null, condition: '权益持续更新',
          gift: { has: false, desc: null, images: [] },
        },
      ],
    },
  ];
}

/* -- 批量演示商品：让今晚李佳琦场次达到 60+ 条以驱动分页 -- */
function buildBulkGoods(): SessionProduct[] {
  const s = SESSIONS[1];
  const pool: { name: string; brand: string; cat: string; price: string | null }[] = [
    { name: '焕活修护精华露 50ml', brand: '兰蔻', cat: 'beauty', price: '780' },
    { name: '小黑瓶发光眼霜 15ml', brand: '兰蔻', cat: 'beauty', price: '520' },
    { name: '水杨酸净澈洁面 120g', brand: '宝拉珍选', cat: 'beauty', price: '169' },
    { name: '轻盈持妆粉底液 30ml', brand: 'MAKE UP FOR EVER', cat: 'beauty', price: '350' },
    { name: '深海鱼油软胶囊 200粒', brand: 'Swisse', cat: 'food', price: '299' },
    { name: '益生菌固体饮料 30条', brand: '合生元', cat: 'food', price: '328' },
    { name: '即食鸡胸肉 100g×7袋', brand: '优形', cat: 'food', price: '99' },
    { name: '除菌洗衣凝珠 400颗', brand: '立白', cat: 'home', price: '79' },
    { name: '抗菌砧板套装 三件', brand: '双枪', cat: 'home', price: '129' },
    { name: '恒温电热水壶 1.5L', brand: '美的', cat: 'home', price: '199' },
    { name: '羊毛混纺围巾 燕麦色', brand: '鄂尔多斯', cat: 'fashion', price: '420' },
    { name: '轻便羽绒马甲 男女同款', brand: '优衣库', cat: 'fashion', price: '299' },
    { name: '真皮乐福鞋 圆头浅口', brand: '百丽', cat: 'fashion', price: '459' },
    { name: '男士加厚保暖内衣套装', brand: '恒源祥', cat: 'fashion', price: '189' },
    { name: '纯棉抗菌内裤 4条装', brand: '蕉内', cat: 'fashion', price: '139' },
    { name: '胶囊咖啡机 家用mini', brand: '奈斯派索', cat: 'home', price: '899' },
    { name: '护肝片 120片', brand: 'Swisse', cat: 'food', price: '259' },
    { name: '玻尿酸补水面膜 30片', brand: '敷尔佳', cat: 'beauty', price: '239' },
  ];
  const out: SessionProduct[] = [];
  for (let i = 0; i < 60; i++) {
    const base = pool[i % pool.length];
    const hasGift = i % 3 !== 0;
    const noPrice = i % 11 === 10;
    out.push({
      sessionProductId: `sp-bulk-${i + 1}`,
      session: s,
      linkNo: 10 + i, // 序号从 10 号起，避开演示的前几个
      itemId: `demo-bulk-${i + 1}`,
      itemName: `${base.brand} ${base.name}`,
      brand: base.brand,
      categoryId: base.cat,
      image: DEMO_IMG,
      offers: [
        {
          specId: `o-bulk-${i + 1}-1`,
          name: i % 2 === 0 ? '常规装' : '加大装',
          planGroup: null,
          buyCount: 1,
          toHandQty: i % 2 === 0 ? 1 : 2,
          toHandUnit: '件',
          price: noPrice ? null : String(Math.max(59, parseInt(base.price ?? '199', 10) - (i % 7) * 10)),
          condition: noPrice ? '权益持续更新' : hasGift ? '拍1件，赠品随单' : '拍1件，活动价',
          gift: {
            has: hasGift,
            desc: hasGift ? '品牌小样 × 2' : null,
            images: hasGift ? [DEMO_GIFT] : [],
          },
        },
      ],
    });
  }
  return out;
}


/* -- 蜜蜂 surprise 社（紧凑行） -- */
export const GOODS_TODAY_BEE: SessionProduct[] = [
  {
    sessionProductId: idNext(),
    session: SESSIONS[2],
    linkNo: 354,
    itemId: 'demo-2001',
    itemName: '舒适达抗敏感牙龈护理牙膏 100g×3支',
    brand: '舒适达',
    categoryId: 'home',
    image: DEMO_IMG,
    offers: [
      {
        specId: 'o-2001-1', name: '100g × 3支', planGroup: null,
        buyCount: 1, toHandQty: 5, toHandUnit: '支',
        price: '99', condition: '赠旅行装2支',
        gift: { has: true, desc: '旅行装2支', images: [DEMO_GIFT] },
      },
    ],
  },
  {
    sessionProductId: idNext(),
    session: SESSIONS[2],
    linkNo: 496,
    itemId: 'demo-2002',
    itemName: '骆驼三合一冲锋衣 · 抓绒内胆',
    brand: '骆驼',
    categoryId: 'fashion',
    image: DEMO_IMG,
    offers: [
      {
        specId: 'o-2002-1', name: '多色可选', planGroup: null,
        buyCount: 1, toHandQty: 1, toHandUnit: '件',
        price: '399', condition: '到手价 · 多色可选',
        gift: { has: false, desc: null, images: [] },
      },
    ],
  },
  {
    sessionProductId: idNext(),
    session: SESSIONS[2],
    linkNo: 452,
    itemId: 'demo-2003',
    itemName: '淘淘氧棉纯棉日用卫生巾组合装',
    brand: '淘淘氧棉',
    categoryId: 'home',
    image: DEMO_IMG,
    offers: [
      {
        specId: 'o-2003-1', name: '日用组合 · 需拍3组', planGroup: null,
        buyCount: 3, toHandQty: 312, toHandUnit: '片',
        price: '94', condition: '拍3组共312片 · 总价282元',
        gift: { has: true, desc: '护手霜45g × 1支', images: [DEMO_GIFT] },
      },
    ],
  },
];

/* 香菇来了（紧凑行） */
export const GOODS_TODAY_MUSHROOM: SessionProduct[] = [
  {
    sessionProductId: idNext(),
    session: SESSIONS[3],
    linkNo: 201,
    itemId: 'demo-3001',
    itemName: '秋羽绒轻暖连帽外套',
    brand: '秋水伊人',
    categoryId: 'fashion',
    image: DEMO_IMG,
    offers: [
      {
        specId: 'o-3001-1', name: '米白/豆绿 · M-2XL', planGroup: null,
        buyCount: 1, toHandQty: 1, toHandUnit: '件',
        price: '259', condition: '到手价 · 可叠券',
        gift: { has: true, desc: '便携收纳袋 × 1', images: [DEMO_GIFT] },
      },
    ],
  },
];

/* 明天李佳琦 */
export const GOODS_TOMORROW_LI: SessionProduct[] = [
  {
    sessionProductId: 'sp-tom-1',
    session: SESSIONS[4],
    linkNo: 9,
    itemId: 'demo-1401',
    itemName: '焕新日重磅彩妆套组',
    brand: '兰蔻',
    categoryId: 'beauty',
    image: DEMO_IMG,
    offers: [
      {
        specId: 'o-1401-1', name: '精华+面霜礼盒', planGroup: null,
        buyCount: 1, toHandQty: 2, toHandUnit: '件',
        price: '650', condition: '拍1件，赠香水小样',
        gift: { has: true, desc: '香水小样 × 2', images: [DEMO_GIFT] },
      },
    ],
  },
];

/* ===== 商品索引 ===== */
export const ALL_GOODS: SessionProduct[] = [
  ...GOODS_TODAY_LI,
  ...GOODS_TODAY_BEE,
  ...GOODS_TODAY_MUSHROOM,
  ...buildBulkGoods(),
].concat(GOODS_TOMORROW_LI);

export function goodsOf(sessionId: string): SessionProduct[] {
  return ALL_GOODS.filter((g) => g.session.id === sessionId);
}

/** C02：场次卡预览（最多3个；0件不伪造预览） */
export function previewOf(sessionId: string): SessionProduct[] {
  return goodsOf(sessionId).slice(0, 3);
}

export function findProduct(id: string): SessionProduct | undefined {
  return ALL_GOODS.find((g) => g.sessionProductId === id);
}

/* ===== 精选（F01：按主题分组、含来源） ===== */
const FEATURED_SECTIONS: { theme: string; items: string[] }[] = [
  { theme: '秋日护肤', items: ['sp-3'] },
  { theme: '家庭日用', items: ['sp-5'] },
  { theme: '多规格好物', items: ['sp-1'] },
  { theme: '囤货组合', items: ['sp-7'] },
];
export function featuredList(): Recommendation[] {
  const out: Recommendation[] = [];
  FEATURED_SECTIONS.forEach((sec, i) => {
    sec.items.forEach((spid) => {
      const item = findProduct(spid);
      if (item) out.push({ id: `rec-${i}`, sessionProductId: spid, theme: sec.theme, item });
    });
  });
  return out;
}
